import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty, range } from 'lodash-es';
import styled from 'styled-components';

import { BasicInput, SimplePopover, SimpleToggle, Typography, TabsPanel } from '@dev/ee-components';

import { getPoolsStore } from '@/store/pools/actions';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import { useIsTypeAB } from '@/components/CampaignCreator/hooks/useIsTypeAB';
import { getCampainCreatorStore, ModifyCampaignMap } from '@/store/campaignCreator/actions';
import { DEFAULT_TAB_ID } from '@/services/constants';
import { useGetSettingsInfo } from '../../../../hooks/useGetStep3Info';
import { ToggleWrapper } from '../../../../../styled';
import { useIPpoolsArray } from '../../../../hooks/useIPPoolsArray';
import { CustomPopoverBody } from './CustomPopoverBody';
import { ManageIpsTabsWrapper } from './styles';

const ManageIpsDropdownWrapper = styled.div`
    max-width: 760px;
    margin-top: 16px;
    margin-left: 60px;
`;

export const ManageIPPools = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isTypeAB = useIsTypeAB();
    const { ipPools } = useSelector(getPoolsStore);
    const { abTabs, campaign } = useSelector(getCampainCreatorStore);
    const [currentTab, setCurrentTab] = useState<string>(DEFAULT_TAB_ID);
    const options = useIPpoolsArray(ipPools);
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const { poolName } = useGetSettingsInfo(parseInt(currentTab, 10));
    const [manageIPpoolsToggle, setManageIppoolsToggle] = useState(
        campaign?.CampaignTemplates?.findIndex(
            (template) => !(template.Poolname === null || template.Poolname === 'default')
        ) > -1
    );

    useEffect((): void => {
        if (!abTabs?.length) {
            return;
        }
        if (!currentTab || abTabs.findIndex((item): boolean => item.id === currentTab) === -1) {
            setCurrentTab(abTabs[0].id);
        }
    }, [abTabs]);

    if (isEmpty(ipPools)) {
        return null;
    }

    const currentTemplatePoolNamePath = (index: string | number): string => `CampaignTemplates[${index}].Poolname`;

    const changeToggleHandler = (ev): void => {
        setManageIppoolsToggle(ev.target.checked);
        if (!ev.target.checked) {
            const modifyMap = new Map<string, any>();

            if (abTabs.length < 1) {
                modifyMap.set(currentTemplatePoolNamePath(currentTab), null);
                dispatch(ModifyCampaignMap(modifyMap));
                return;
            }

            const allTabs = range(0, abTabs.length);
            allTabs.forEach((index): void => {
                modifyMap.set(currentTemplatePoolNamePath(index), null);
            });

            dispatch(ModifyCampaignMap(modifyMap));
        }
    };
    const clickItemHandler = (e, obj): void => {
        const modifyMap = new Map<string, any>();
        modifyMap.set(currentTemplatePoolNamePath(currentTab), obj.value);
        dispatch(ModifyCampaignMap(modifyMap));
    };

    const onTabChange = (event: React.ChangeEvent<HTMLElement>, value: string | number): void => {
        setCurrentTab(value as string);
    };

    return (
        <>
            <ToggleWrapper>
                <SimpleToggle
                    id="manage-ippools-toggle"
                    value="ippools"
                    onChange={changeToggleHandler}
                    checked={manageIPpoolsToggle}
                    disabled={isDetailsScreen}
                />
                <Typography variant="h3">{t('manageippools')}</Typography>
            </ToggleWrapper>
            {manageIPpoolsToggle && (
                <ManageIpsDropdownWrapper>
                    <>
                        {isTypeAB && (
                            <ManageIpsTabsWrapper>
                                <TabsPanel
                                    isMobile={false}
                                    value={currentTab}
                                    onChange={onTabChange}
                                    tabs={abTabs.map((item) => ({
                                        label: item.tabTitle,
                                        value: item.id,
                                    }))}
                                    defaultUseTabsValues
                                    disableSentenceCase
                                />
                            </ManageIpsTabsWrapper>
                        )}
                        {isDetailsScreen ? (
                            <BasicInput value={poolName} readOnly />
                        ) : (
                            <SimplePopover
                                // eslint-disable-next-line react/no-unstable-nested-components
                                customDropdownBody={(element: any): JSX.Element => (
                                    <CustomPopoverBody element={element} />
                                )}
                                id="ip-pools-dropdown"
                                listItemWidth={350}
                                maxHeight={400}
                                options={options}
                                value={poolName}
                                type="input"
                                placement="bottom-start"
                                fullWidth
                                defaultValue={poolName}
                                onItemClick={clickItemHandler}
                            />
                        )}
                    </>
                </ManageIpsDropdownWrapper>
            )}
        </>
    );
};
